import Icon from "./Icon.jsx";
import Badge from "./Badge.jsx";

/**
 * CollapsibleSection component
 *
 * Renders a collapsible section with a header, an animated chevron toggle icon,
 * and a summary badge.
 *
 * @param {object} props
 * @param {string} props.title - The title of the section.
 * @param {string} [props.badgeText] - The text displayed inside the status badge.
 * @param {string} [props.badgeColor='oslo'] - The color style of the badge.
 * @param {boolean} props.isExpanded - Controls whether the body is shown.
 * @param {function} props.onToggle - Callback function when header is clicked to expand/collapse.
 * @param {React.ReactNode} props.children - Section contents.
 */
export default function CollapsibleSection({
    title,
    badgeText,
    badgeColor = "oslo",
    isExpanded,
    onToggle,
    canToggle = true,
    children,
}) {
    return (
        <div className="w-full flex flex-col overflow-hidden gap-5">
            {/* Header */}
            <div className="flex flex-row justify-between items-center">
                <p className="font-ui font-header text-body-base text-main-typography-system-default">
                    {title}
                </p>
                <div className="flex items-center gap-3">
                    {badgeText && <Badge color={badgeColor} size="sm" text={badgeText} />}
                    {canToggle && (
                        <div
                            className="w-[32px] h-[32px] flex items-center justify-center"
                            onClick={canToggle ? onToggle : undefined}
                        >
                            <Icon
                                name={isExpanded ? "chevron-up" : "chevron-down"}
                                size="sm"
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* Body */}
            {canToggle ? (
                <div className={`${isExpanded ? "" : "hidden"}`}>{children}</div>
            ) : (
                <div>{children}</div>
            )}
        </div>
    );
}
